<template>
  <div>
    <only-site-card :com_data="com_data">
      <template v-slot:item_edit>
        <ValidationObserver ref="observer">
          <v-container fluid>
            <v-row>
              <v-col cols="12" sm="2">
                <span class="font-weight-bold">メッセージ</span>
                <p class="red--text mini">
                  未入力の場合、コメントタブの「<strong>お店コメント</strong>」の内容で更新します。
                </p>
              </v-col>
              <v-col cols="12" sm="10">
                <v-row>
                  <v-col cols="12">
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      :name="items.message.text"
                      :rules="items.message.rules"
                    >
                      <v-textarea
                        ref="message"
                        v-model="cast_prop.message"
                        name="message"
                        :error-messages="errors"
                        :success="valid"
                        :label="items.message.text"
                        rows="4"
                        outlined
                        :counter="items.message.counter"
                        background-color="white"
                      />
                    </ValidationProvider>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-divider class="mt-4 mb-10"></v-divider>
            <v-row>
              <v-col cols="12" sm="2">
                <span class="font-weight-bold">新人設定</span>
                <p class="red--text mini">
                  表示期間を過ぎると、媒体側で新人設定が解除されます。
                </p>
              </v-col>
              <v-col cols="12" sm="10">
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-switch
                      class="mt-1"
                      ref="newbie_flag"
                      v-model="cast_prop.newbie_flag"
                      name="newbie_flag"
                      :label="items.newbie_flag.text"
                    ></v-switch>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      :name="items.newbie_expire_date.text"
                      :rules="items.newbie_expire_date.rules"
                      :disabled="!cast_prop.newbie_flag"
                    >
                      <v-text-field
                        ref="newbie_expire_date"
                        v-model="cast_prop.newbie_expire_date"
                        name="newbie_expire_date"
                        :error-messages="errors"
                        :success="valid"
                        :label="items.newbie_expire_date.text"
                        type="date"
                        outlined
                        clearable
                        background-color="white"
                        hint="※空欄の場合は常時「新人」として表示されます"
                        persistent-hint
                        v-show="cast_prop.newbie_flag"
                      ></v-text-field>
                    </ValidationProvider>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-container>
        </ValidationObserver>
      </template>
    </only-site-card>
  </div>
</template>

<script>
import {
  set,
  ref,
  reactive,
  toRefs,
  defineComponent,
} from "@vue/composition-api";
import { repositoryFactory } from "@/repository/repositoryFactory";
import store from "@/store/index.js";
import setting from "@/common/setting.js";
import utilFunc from "@/common/utilFunc.js";
import selectItem from "@/common/selectItem.js";

export default defineComponent({
  props: ["com_data"],
  setup(props, ctx) {
    const CastPropsRepository = repositoryFactory.get("castProps");
    const state = reactive({
      errorElement: null,
      cast_prop: {},
      // 読み込み時データ保持(保存前比較用)
      keepData: {
        cast_prop: {},
      },
    });

    const items = {
      "message": {
        text: "メッセージ",
        rules: "max:10000",
        counter: "10000",
      },
      "newbie_flag": {
        text: "このセラピストを新人として表示する",
        rules: "",
        counter: "",
      },
      "newbie_expire_date": {
        text: "表示期間",
        rules: "max_month_from_today:3",
        counter: "",
      },
    };

    const init = async () => {
      // cast_propにキーを設定
      // ※新人設定は媒体側の初期値が「新人」の為、未設定(false)を既定とする
      for (let key in items) {
        set(state.cast_prop, key, key == "newbie_flag" ? false : null);
      }
      Object.keys(props.com_data.cast_prop).forEach(function (key) {
        if (key == "newbie_flag") {
          set(state.cast_prop, key, props.com_data.cast_prop[key] == "True");
        } else {
          set(state.cast_prop, key, props.com_data.cast_prop[key]);
        }
      });
      // 読み込み時のデータを保持(保存前に比較するため)
      state.keepData.cast_prop = Object.assign({}, state.cast_prop);
    };

    init();

    // チェック処理
    const checkValid = async () => {
      // 事前チェック処理
      if (state.cast_prop.newbie_flag == false) {
        set(state.cast_prop, "newbie_expire_date", null);
      }
      state.errorElement = null;
      const isValid = await ctx.refs.observer.validate();
      if (!isValid) {
        let item_name = "";
        Object.keys(ctx.refs.observer.errors).forEach(function (key) {
          if (ctx.refs.observer.errors[key].length > 0) {
            item_name = key;
            return;
          }
        });
        const errorKey = utilFunc.getKeyByItemText(items, item_name);
        state.errorElement = ctx.refs[errorKey].$el;
      }
      return isValid;
    };

    const setErrorFocus = async () => {
      setTimeout(() => {
        state.errorElement.scrollIntoView({
          block: "center",
        });
      }, 300);
    };

    const save = async () => {
      // 更新前と比べて差分があれば更新処理
      if (!utilFunc.compareObject(state.keepData.cast_prop, state.cast_prop)) {
        const prop_param = [];
        for (let key in state.cast_prop) {
          if (key == "newbie_flag") {
            prop_param.push({
              cast_id: props.com_data.cast_id,
              key: props.com_data.prefix + "__" + key,
              value: state.cast_prop[key] ? "True" : "False",
            });
          } else {
            prop_param.push({
              cast_id: props.com_data.cast_id,
              key: props.com_data.prefix + "__" + key,
              value: state.cast_prop[key] ?? "",
            });
          }
        }
        await CastPropsRepository.save_all({ cast_prop: prop_param })
          .then((response) => {
            if (response.data) {
              // データ追加OK
            }
          })
          .catch((error) => {
            throw (
              "ERROR:cast_manager_wordpress.vue/save CastPropsRepository.save_all (" +
              error +
              ")"
            );
          });
      }
      return true;
    };

    return {
      props,
      selectItem,
      setting,
      items,
      ...toRefs(state),
      checkValid,
      setErrorFocus,
      save,
    };
  },
});
</script>

<style scoped></style>
